// Protection du contenu des tutoriels exposés publiquement.
//
// Ce que ce module bloque réellement :
//   - la sélection et la copie du TEXTE rédactionnel (les blocs de code restent copiables) ;
//   - le clic droit, le glisser-déposer et l'enregistrement des images ;
//   - l'impression et l'enregistrement de la page (Ctrl+P, Ctrl+S) ;
//   - les raccourcis d'ouverture du code source et des outils de développement.
//
// Ce qu'AUCUNE page web ne peut bloquer de façon absolue : une capture d'écran ou
// un enregistrement vidéo, réalisés au niveau du système d'exploitation (il n'existe
// pas d'équivalent web au FLAG_SECURE d'Android).
// La stratégie retenue est donc : NOIRCIR l'écran dès qu'une capture est détectable,
// et rendre traçable ce qui passerait malgré tout.
//   - masquage noir opaque quand la page perd le focus (la plupart des outils de
//     capture — Outil Capture d'écran, Lightshot, Greenshot, Win+Shift+S — prennent
//     le focus : la capture ne contient alors que du noir) ;
//   - masquage noir immédiat à l'appui sur Impr. écran, + neutralisation du presse-papiers ;
//   - filigrane permanent portant l'identité du lecteur et l'horodatage, pour les
//     cas non détectables (Impr. écran plein écran, caméra, enregistreur externe) ;
//   - journalisation de toutes les tentatives, consultable par l'administration.
import { useEffect, useRef, useState, useCallback } from 'react';
import { ShieldAlert } from 'lucide-react';
import './content-protection.css';

const CLASSE_PROTEGE = 'tut-protected';
const CLASSE_MASQUE = 'tut-protected-mask';
// Durée du masquage noir après un appui sur Impr. écran : la capture doit
// être prise pendant que l'écran est noir.
const DUREE_MASQUE_CAPTURE = 2500;
// Sélecteur des zones où la sélection reste autorisée : les commandes.
const ZONES_AUTORISEES = '.tut-code-card, pre, code';

// ==================== JOURNALISATION ====================

const DERNIER_ENVOI = {};
const DELAI_ANTI_SPAM = 10000; // un même type d'événement au plus toutes les 10 s

function journaliser(pageId, type, details = {}) {
  if (!pageId) return;
  const maintenant = Date.now();
  if (DERNIER_ENVOI[type] && maintenant - DERNIER_ENVOI[type] < DELAI_ANTI_SPAM) return;
  DERNIER_ENVOI[type] = maintenant;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const corps = JSON.stringify({
    event_type: type,
    page_id: pageId,
    details,
    viewer_id: details.viewerId || null,
    viewer_label: details.viewerLabel || null,
    session_ref: details.sessionRef || null,
    url: window.location.href,
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
  });

  // sendBeacon survit à une fermeture d'onglet ; fetch en repli
  try {
    const url = `${backendUrl}/api/tutorials/public/${pageId}/protection-event`;
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([corps], { type: 'application/json' }));
    } else {
      fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: corps, keepalive: true });
    }
  } catch { /* la protection ne doit jamais casser l'affichage */ }
}

// ==================== FILIGRANE ====================

function construireFiligrane(libelle) {
  const texte = libelle.replace(/[<>&"]/g, '');
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="460" height="230">
    <text x="18" y="150" font-family="system-ui,sans-serif" font-size="13" font-weight="600"
          fill="rgba(99,102,241,0.16)" transform="rotate(-24 18 150)">${texte}</text>
  </svg>`;
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}")`;
}

function horodatage() {
  return new Date().toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Référence de session courte : permet de relier une fuite à une consultation
function refSession() {
  try {
    let ref = sessionStorage.getItem('tut-session-ref');
    if (!ref) {
      ref = Math.random().toString(36).slice(2, 8).toUpperCase();
      sessionStorage.setItem('tut-session-ref', ref);
    }
    return ref;
  } catch {
    return 'ANON';
  }
}

// ==================== COMPOSANT ====================

/**
 * @param {object}   props
 * @param {object}   props.containerRef  conteneur de l'article à protéger
 * @param {string}   props.pageId        identifiant de la page (journalisation)
 * @param {object}   props.viewer        { id, label } identité du lecteur si connue
 * @param {boolean}  props.enabled       false pour désactiver (administrateurs)
 */
export default function ContentProtection({ containerRef, pageId, viewer, enabled = true }) {
  const [alerte, setAlerte] = useState(null);
  const [masque, setMasque] = useState(false);
  const [tampon, setTampon] = useState(() => horodatage());
  const alerteTimer = useRef(null);
  const masqueTimer = useRef(null);
  const masqueVerrouille = useRef(false);   // masquage temporisé, non levé par un simple focus
  const sessionRef = useRef(refSession());

  const contexte = useCallback(() => ({
    viewerId: viewer?.id || null,
    viewerLabel: viewer?.label || 'visiteur non identifié',
    sessionRef: sessionRef.current,
  }), [viewer]);

  const avertir = useCallback((message, type, details = {}) => {
    setAlerte(message);
    clearTimeout(alerteTimer.current);
    alerteTimer.current = setTimeout(() => setAlerte(null), 4000);
    journaliser(pageId, type, { ...contexte(), ...details });
  }, [pageId, contexte]);

  // Noircit l'écran. Avec `duree`, le masque est verrouillé le temps indiqué :
  // indispensable pour une capture, qui est prise juste après l'appui sur la touche.
  const noircir = useCallback((duree = 0) => {
    setMasque(true);
    clearTimeout(masqueTimer.current);
    if (duree > 0) {
      masqueVerrouille.current = true;
      masqueTimer.current = setTimeout(() => {
        masqueVerrouille.current = false;
        if (!document.hidden && document.hasFocus()) setMasque(false);
      }, duree);
    }
  }, []);

  const devoiler = useCallback(() => {
    if (masqueVerrouille.current) return;      // masquage de capture en cours
    setMasque(false);
  }, []);

  // --- Rafraîchit l'horodatage du filigrane : une capture est toujours datée
  useEffect(() => {
    if (!enabled) return;
    const timer = setInterval(() => setTampon(horodatage()), 30000);
    return () => clearInterval(timer);
  }, [enabled]);

  // --- Protections liées au contenu.
  // Les écouteurs sont posés sur `document` et non sur l'article : le titre,
  // le sommaire et la galerie doivent être protégés eux aussi.
  useEffect(() => {
    if (!enabled) return;
    const conteneur = containerRef?.current;

    document.body.classList.add(CLASSE_PROTEGE);

    // Une sélection est autorisée si elle est ENTIÈREMENT dans un bloc de code
    const dansZoneAutorisee = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return false;
      const noeud = sel.getRangeAt(0).commonAncestorContainer;
      const el = noeud.nodeType === 1 ? noeud : noeud.parentElement;
      return Boolean(el?.closest(ZONES_AUTORISEES));
    };

    const surCopie = (e) => {
      if (dansZoneAutorisee()) return;          // commandes : copie autorisée
      e.preventDefault();
      try {
        e.clipboardData?.setData('text/plain',
          `Contenu protégé — EFIlearn (réf. ${sessionRef.current})`);
      } catch { /* ignore */ }
      avertir('Copie du contenu désactivée. Les blocs de commandes restent copiables via le bouton « copier ».',
        'copy_blocked');
    };

    const surMenuContextuel = (e) => {
      if (e.target.closest?.(ZONES_AUTORISEES)) return;   // clic droit utile sur les commandes
      e.preventDefault();
      const surImage = e.target.tagName === 'IMG';
      avertir(surImage
        ? 'Enregistrement des images désactivé.'
        : 'Menu contextuel désactivé sur ce contenu.',
        surImage ? 'image_save_blocked' : 'contextmenu_blocked');
    };

    const surGlisser = (e) => {
      e.preventDefault();
      journaliser(pageId, 'drag_blocked', contexte());
    };

    document.addEventListener('copy', surCopie, true);
    document.addEventListener('cut', surCopie, true);
    document.addEventListener('contextmenu', surMenuContextuel, true);
    document.addEventListener('dragstart', surGlisser, true);

    // Les images ne doivent être ni glissables ni cibles d'un appui long (mobile)
    const protegerImages = () => {
      document.querySelectorAll('.tut-public-page img').forEach((img) => {
        img.setAttribute('draggable', 'false');
        img.classList.add('tut-protected-img');
      });
    };
    protegerImages();
    // L'article est rendu par TipTap après coup : on surveille son arrivée
    const observateur = new MutationObserver(protegerImages);
    if (conteneur) observateur.observe(conteneur, { childList: true, subtree: true });

    return () => {
      document.body.classList.remove(CLASSE_PROTEGE);
      document.removeEventListener('copy', surCopie, true);
      document.removeEventListener('cut', surCopie, true);
      document.removeEventListener('contextmenu', surMenuContextuel, true);
      document.removeEventListener('dragstart', surGlisser, true);
      observateur.disconnect();
    };
  }, [enabled, containerRef, pageId, avertir, contexte]);

  // --- Raccourcis clavier et touche Impr. écran
  useEffect(() => {
    if (!enabled) return;

    const dansZoneAutorisee = () => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return false;
      const noeud = sel.getRangeAt(0).commonAncestorContainer;
      const el = noeud.nodeType === 1 ? noeud : noeud.parentElement;
      return Boolean(el?.closest(ZONES_AUTORISEES));
    };

    const neutraliserPressePapiers = () => {
      try {
        navigator.clipboard?.writeText(
          `Contenu protégé — EFIlearn (réf. ${sessionRef.current} — ${horodatage()})`
        ).catch(() => {});
      } catch { /* ignore */ }
    };

    const surTouche = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      const touche = (e.key || '').toLowerCase();

      // Impr. écran : on noircit IMMÉDIATEMENT, avant que la capture ne soit prise,
      // et on neutralise le presse-papiers. Déclenché sur keydown comme sur keyup.
      if (e.key === 'PrintScreen' || e.code === 'PrintScreen') {
        noircir(DUREE_MASQUE_CAPTURE);
        neutraliserPressePapiers();
        avertir('Capture d\'écran détectée : l\'écran a été masqué et la tentative est journalisée.',
          'screenshot_attempt');
        return;
      }

      // Raccourcis de capture système (Win+Shift+S, macOS Cmd+Shift+3/4/5).
      // Le navigateur ne les reçoit pas toujours ; quand c'est le cas, on noircit.
      if (e.shiftKey && (e.metaKey || e.key === 'Meta') && ['s', '3', '4', '5'].includes(touche)) {
        noircir(DUREE_MASQUE_CAPTURE);
        avertir('Outil de capture détecté : l\'écran a été masqué.', 'screenshot_attempt',
          { raccourci: touche });
        return;
      }

      if (!mod) {
        if (e.key === 'F12') {
          e.preventDefault();
          journaliser(pageId, 'devtools_shortcut', contexte());
        }
        return;
      }

      // Ctrl/Cmd + C ou X hors bloc de code
      if ((touche === 'c' || touche === 'x') && !dansZoneAutorisee()) {
        e.preventDefault();
        avertir('Copie du contenu désactivée.', 'copy_blocked', { via: 'clavier' });
        return;
      }
      // Enregistrement de la page, impression, code source
      if (['s', 'p', 'u'].includes(touche)) {
        e.preventDefault();
        const libelles = { s: 'Enregistrement de la page', p: 'Impression', u: 'Affichage du code source' };
        avertir(`${libelles[touche]} désactivé${touche === 'p' ? 'e' : ''}.`, `${touche}_blocked`);
        return;
      }
      // Outils de développement
      if (e.shiftKey && ['i', 'j', 'c'].includes(touche)) {
        e.preventDefault();
        journaliser(pageId, 'devtools_shortcut', contexte());
      }
    };

    const surImpression = () => journaliser(pageId, 'print_attempt', contexte());

    window.addEventListener('keydown', surTouche, true);
    window.addEventListener('keyup', surTouche, true);
    window.addEventListener('beforeprint', surImpression);

    return () => {
      window.removeEventListener('keydown', surTouche, true);
      window.removeEventListener('keyup', surTouche, true);
      window.removeEventListener('beforeprint', surImpression);
      clearTimeout(masqueTimer.current);
    };
  }, [enabled, pageId, avertir, contexte, noircir]);

  // --- Masquage noir à la perte de focus.
  // La plupart des outils de capture prennent le focus au lancement : la capture
  // ne contient alors que l'écran noir.
  useEffect(() => {
    if (!enabled) return;

    const surPerteFocus = () => noircir();
    const surVisibilite = () => {
      if (document.hidden) {
        noircir();
        journaliser(pageId, 'focus_lost', contexte());
      } else {
        devoiler();
      }
    };

    window.addEventListener('blur', surPerteFocus);
    window.addEventListener('focus', devoiler);
    document.addEventListener('visibilitychange', surVisibilite);

    return () => {
      window.removeEventListener('blur', surPerteFocus);
      window.removeEventListener('focus', devoiler);
      document.removeEventListener('visibilitychange', surVisibilite);
    };
  }, [enabled, pageId, contexte, noircir, devoiler]);

  // --- Application de la classe de masquage sur le body
  useEffect(() => {
    document.body.classList.toggle(CLASSE_MASQUE, Boolean(enabled && masque));
    return () => document.body.classList.remove(CLASSE_MASQUE);
  }, [enabled, masque]);

  // --- Ouverture de la page consignée une fois
  useEffect(() => {
    if (!enabled || !pageId) return;
    journaliser(pageId, 'page_view', contexte());
  }, [enabled, pageId, contexte]);

  if (!enabled) return null;

  const libelle = `${viewer?.label || 'Lien public'} · ${tampon} · réf. ${sessionRef.current}`;

  return (
    <>
      {/* Filigrane : toute capture porte l'identité du lecteur et la date */}
      <div
        className="tut-watermark"
        aria-hidden="true"
        style={{ backgroundImage: construireFiligrane(libelle) }}
      />

      {/* Masque noir opaque : une capture prise à cet instant ne contient que du noir */}
      {masque && (
        <div className="tut-mask" onClick={devoiler}>
          <div className="tut-mask-inner">
            <ShieldAlert size={26} />
            <strong>Contenu masqué</strong>
            <span>Cliquez sur la page ou revenez sur cet onglet pour l'afficher.</span>
          </div>
        </div>
      )}

      {/* Message transitoire lors d'une tentative bloquée */}
      {alerte && (
        <div className="tut-protect-toast" role="status">
          <ShieldAlert size={16} />
          <span>{alerte}</span>
        </div>
      )}

      {/* Mention de protection, affichée en bas de page */}
      <div className="tut-protect-badge" title={`Réf. de consultation : ${sessionRef.current}`}>
        <ShieldAlert size={12} />
        Contenu protégé — {libelle}
      </div>
    </>
  );
}
